import {Spec} from 'immutability-helper';

import {StringIDFolder} from 'constants/FolderConstants';
import {SidepanelSection} from 'constants/NavConstants';
import {Link} from 'link-preview/LinkDAO';
import {DocumentUpdateOptions} from 'notebook/documents/DocumentActions';
import {Document} from 'notebook/documents/DocumentConstants';
import {Timer} from 'notebook/protocols/timers/TimerTypes';
import {StructuredTableMappingActionOptions} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingConstants';
import {TableCellEditorPosition} from 'notebook/tables/constants/CellConstants';
import {TableOpDiffMap} from 'notebook/tables/excess/ops/TableOpConstants';
import {Section as SidebarSection} from 'sidebar/SidebarConstants';
import {TrayTaskOptions} from 'tray/TrayTaskTypes';

/**
 * An untyped flux action payload.
 *
 * We are incrementally migrating our legacy Flux actions to TypeScript and the events which are not yet
 * strongly typed use this.
 */
// oxlint-disable-next-line @typescript-eslint/no-explicit-any
type UntypedPayload = Record<string, any>;

/**
 * An empty payload for events that carry no data.
 */
type EmptyPayload = Record<string, never>;

/**
 * A map of all possible event names and their payload types. This constant should not be exported or used
 * directly outside this file.
 *
 * We use an object to declare the payloads as it allows us to easily enforce at compile time that each event
 * name is unique and only defined in one place.
 *
 * We then derive our EventConstants (as a string literal enum) and FluxAction (as a discriminated union type)
 * from it.
 */
const EVENT_PAYLOADS = {
  SELECTION_SET: {} as UntypedPayload,

  NOTE_BULK_UPDATE: {} as UntypedPayload,
  NOTE_CLOSE: {} as UntypedPayload,
  NOTE_FETCH_VERSIONS: {} as UntypedPayload,
  NOTE_FETCH_VERSIONS_COMPLETED: {} as UntypedPayload,
  NOTE_FETCH_VERSIONS_FAILED: {} as UntypedPayload,
  NOTE_LOAD: {} as UntypedPayload,
  NOTE_LOAD_VERSION: {} as UntypedPayload,
  NOTE_LOAD_VERSION_COMPLETED: {} as UntypedPayload,
  NOTE_LOAD_VERSION_FAILED: {} as UntypedPayload,
  NOTE_REGISTER_OBJECTS: {} as UntypedPayload,
  NOTE_REPLACE: {} as UntypedPayload,
  NOTE_SAVE: {} as UntypedPayload,
  NOTE_SAVE_COMPLETED: {} as UntypedPayload,
  NOTE_SAVE_FAILED: {} as UntypedPayload,
  NOTE_UPDATE: {} as UntypedPayload,

  DOCUMENT_OPEN: {} as UntypedPayload,
  DOCUMENT_OPEN_MANY: {} as UntypedPayload,
  DOCUMENT_OPEN_COMPLETED: {} as UntypedPayload,
  DOCUMENT_OPEN_COMPLETED_MANY: {} as UntypedPayload,
  DOCUMENT_OPEN_FAILED: {} as UntypedPayload,
  DOCUMENT_OPEN_FAILED_MANY: {} as UntypedPayload,
  DOCUMENT_CLOSE: {} as UntypedPayload,
  DOCUMENT_RENAME: {} as UntypedPayload,
  DOCUMENT_RESET_TO_OPEN_VERSION: {} as UntypedPayload,
  DOCUMENT_REVERT: {} as UntypedPayload,
  DOCUMENT_SAVE: {} as UntypedPayload,
  DOCUMENT_SAVE_MANY: {} as UntypedPayload,
  DOCUMENT_SAVE_COMPLETED: {} as UntypedPayload,
  DOCUMENT_SAVE_COMPLETED_MANY: {} as UntypedPayload,
  DOCUMENT_SAVE_FAILED: {} as UntypedPayload,
  DOCUMENT_LOAD_VERSION: {} as UntypedPayload,
  DOCUMENT_LOAD_VERSION_COMPLETED: {} as UntypedPayload,
  DOCUMENT_LOAD_VERSION_FAILED: {} as UntypedPayload,
  DOCUMENT_FETCH_VERSIONS: {} as UntypedPayload,
  DOCUMENT_FETCH_VERSIONS_COMPLETED: {} as UntypedPayload,
  DOCUMENT_FETCH_VERSIONS_FAILED: {} as UntypedPayload,
  DOCUMENT_REGISTER_OBJECTS: {} as UntypedPayload,
  DOCUMENT_UPDATE: {} as UntypedPayload,
  DOCUMENT_UPDATE_MANY: {} as UntypedPayload,
  DOCUMENT_UPDATE_RELATED_DOCUMENTS: {} as {
    documentId: string;
    documentUpdate: DocumentUpdateSpec;
    relatedDocumentUpdates?: Array<DocumentUpdateSpec>;
    options?: DocumentUpdateOptions;
  },
  DOCUMENT_INVALIDATE: {} as UntypedPayload,
  DOCUMENT_SERVER_SIDE_UPDATE: {} as UntypedPayload,
  DOCUMENT_REGISTER_NAME_OVERRIDE: {} as {documentId: string; nameOverride: string},

  ENTRY_CLOSE: {} as UntypedPayload,
  ENTRY_DELETE: {} as UntypedPayload,
  ENTRY_DELETE_COMPLETED: {} as UntypedPayload,
  ENTRY_DELETE_FAILED: {} as UntypedPayload,
  ENTRY_FETCH_VERSIONS: {} as UntypedPayload,
  ENTRY_FETCH_VERSIONS_COMPLETED: {} as UntypedPayload,
  ENTRY_FETCH_VERSIONS_FAILED: {} as UntypedPayload,
  ENTRY_INVALIDATE: {} as UntypedPayload,
  ENTRY_LOAD_VERSION: {} as UntypedPayload,
  ENTRY_LOAD_VERSION_COMPLETED: {} as UntypedPayload,
  ENTRY_LOAD_VERSION_FAILED: {} as UntypedPayload,
  ENTRY_OPEN: {} as UntypedPayload,
  ENTRY_OPEN_COMPLETED: {} as UntypedPayload,
  ENTRY_OPEN_FAILED: {} as UntypedPayload,
  ENTRY_REGISTER_OBJECTS: {} as UntypedPayload,
  ENTRY_RESET_TO_OPEN_VERSION: {} as UntypedPayload,
  ENTRY_REVERT: {} as UntypedPayload,
  ENTRY_SAVE: {} as UntypedPayload,
  ENTRY_SAVE_COMPLETED: {} as UntypedPayload,
  ENTRY_SAVE_FAILED: {} as UntypedPayload,
  ENTRY_SERVER_SIDE_UPDATE: {} as UntypedPayload,
  ENTRY_UPDATE: {} as UntypedPayload,
  ENTRY_COLLAB_JOIN: {} as UntypedPayload,
  ENTRY_COLLAB_LEAVE: {} as UntypedPayload,
  ENTRY_INVOKE_PROCESS_TASK_EXECUTION_TABLE_MAPPING: {} as UntypedPayload,
  ENTRY_SET_FOCUSED_TABLE: {} as UntypedPayload,

  STRUCTURED_TABLE_INVOKE_MAPPINGS: {} as {
    fileId: string;
    sourceTableProvenanceUuid: string;
    options: StructuredTableMappingActionOptions;
  },
  STRUCTURED_TABLE_VALIDATION_ERRORS: {} as UntypedPayload,

  EXCESS_DELETE_SNAPSHOTTED_RESOLUTIONS_FOR_TABLE: {} as {
    itemId: string;
    provenanceUuid: string;
    tableOp: TableOpDiffMap;
    lookupConfigIds?: Array<string>;
  },
  EXCESS_FORCE_EVALUATION: {} as UntypedPayload,

  SUBTEMPLATE_CLOSE: {} as UntypedPayload,
  SUBTEMPLATE_DELETE: {} as UntypedPayload,
  SUBTEMPLATE_DELETE_COMPLETED: {} as UntypedPayload,
  SUBTEMPLATE_DELETE_FAILED: {} as UntypedPayload,
  SUBTEMPLATE_FETCH_VERSIONS: {} as UntypedPayload,
  SUBTEMPLATE_FETCH_VERSIONS_COMPLETED: {} as UntypedPayload,
  SUBTEMPLATE_FETCH_VERSIONS_FAILED: {} as UntypedPayload,
  SUBTEMPLATE_LOAD_VERSION: {} as UntypedPayload,
  SUBTEMPLATE_LOAD_VERSION_COMPLETED: {} as UntypedPayload,
  SUBTEMPLATE_LOAD_VERSION_FAILED: {} as UntypedPayload,
  SUBTEMPLATE_OPEN: {} as UntypedPayload,
  SUBTEMPLATE_OPEN_COMPLETED: {} as UntypedPayload,
  SUBTEMPLATE_OPEN_FAILED: {} as UntypedPayload,
  SUBTEMPLATE_REGISTER_OBJECTS: {} as UntypedPayload,
  SUBTEMPLATE_RESET_TO_OPEN_VERSION: {} as UntypedPayload,
  SUBTEMPLATE_REVERT: {} as UntypedPayload,
  SUBTEMPLATE_SAVE: {} as UntypedPayload,
  SUBTEMPLATE_SAVE_COMPLETED: {} as UntypedPayload,
  SUBTEMPLATE_SAVE_FAILED: {} as UntypedPayload,
  SUBTEMPLATE_UPDATE: {} as UntypedPayload,

  TEMPLATE_CLOSE: {} as UntypedPayload,
  TEMPLATE_DELETE: {} as UntypedPayload,
  TEMPLATE_DELETE_COMPLETED: {} as UntypedPayload,
  TEMPLATE_DELETE_FAILED: {} as UntypedPayload,
  TEMPLATE_FETCH_VERSIONS: {} as UntypedPayload,
  TEMPLATE_FETCH_VERSIONS_COMPLETED: {} as UntypedPayload,
  TEMPLATE_FETCH_VERSIONS_FAILED: {} as UntypedPayload,
  TEMPLATE_LOAD_VERSION: {} as UntypedPayload,
  TEMPLATE_LOAD_VERSION_COMPLETED: {} as UntypedPayload,
  TEMPLATE_LOAD_VERSION_FAILED: {} as UntypedPayload,
  TEMPLATE_OPEN: {} as UntypedPayload,
  TEMPLATE_OPEN_COMPLETED: {} as UntypedPayload,
  TEMPLATE_OPEN_FAILED: {} as UntypedPayload,
  TEMPLATE_REGISTER_OBJECTS: {} as UntypedPayload,
  TEMPLATE_RESET_TO_OPEN_VERSION: {} as UntypedPayload,
  TEMPLATE_REVERT: {} as UntypedPayload,
  TEMPLATE_SAVE: {} as UntypedPayload,
  TEMPLATE_SAVE_COMPLETED: {} as UntypedPayload,
  TEMPLATE_SAVE_FAILED: {} as UntypedPayload,
  TEMPLATE_UPDATE: {} as UntypedPayload,

  TEMPLATE_COLLECTIONS_FETCH: {} as UntypedPayload,
  TEMPLATE_COLLECTIONS_FETCH_COMPLETED: {} as UntypedPayload,
  TEMPLATE_COLLECTIONS_FETCH_FAILED: {} as UntypedPayload,
  TEMPLATE_COLLECTION_FETCH: {} as UntypedPayload,
  TEMPLATE_COLLECTION_FETCH_COMPLETED: {} as UntypedPayload,
  TEMPLATE_COLLECTION_FETCH_FAILED: {} as UntypedPayload,
  TEMPLATE_COLLECTION_UPDATE: {} as UntypedPayload,
  TEMPLATE_COLLECTION_UPDATE_COMPLETED: {} as UntypedPayload,
  TEMPLATE_COLLECTION_UPDATE_FAILED: {} as UntypedPayload,
  TEMPLATE_COLLECTION_DELETE: {} as UntypedPayload,
  TEMPLATE_COLLECTION_DELETE_COMPLETED: {} as UntypedPayload,
  TEMPLATE_COLLECTION_DELETE_FAILED: {} as UntypedPayload,

  EDITOR_UPDATE: {} as UntypedPayload,
  EDITOR_REPLACE: {} as UntypedPayload,
  EDITOR_CLOSE: {} as UntypedPayload,
  EDITOR_FETCH_VERSIONS: {} as UntypedPayload,
  EDITOR_FETCH_VERSIONS_COMPLETED: {} as UntypedPayload,
  EDITOR_FETCH_VERSIONS_FAILED: {} as UntypedPayload,
  EDITOR_LOAD_VERSION: {} as UntypedPayload,
  EDITOR_LOAD_VERSION_COMPLETED: {} as UntypedPayload,
  EDITOR_LOAD_VERSION_FAILED: {} as UntypedPayload,
  EDITOR_REGISTER_OBJECTS: {} as UntypedPayload,
  EDITOR_RESET_TO_OPEN_VERSION: {} as UntypedPayload,
  EDITOR_SAVE: {} as UntypedPayload,
  EDITOR_SAVE_COMPLETED: {} as UntypedPayload,
  EDITOR_SAVE_FAILED: {} as UntypedPayload,

  EDITOR_OPEN: {} as UntypedPayload,
  EDITOR_OPEN_COMPLETED: {} as UntypedPayload,
  EDITOR_OPEN_FAILED: {} as UntypedPayload,

  PROTEIN_AA_UPDATE_COMPLETED: {} as UntypedPayload,
  PROTEIN_ADD_FOR_SEARCH: {} as UntypedPayload,
  PROTEIN_CLEAR_SEARCH_RESULTS: {} as UntypedPayload,
  PROTEIN_DELETE_COMPLETED: {} as UntypedPayload,
  PROTEIN_FETCH_VERSIONS: {} as UntypedPayload,
  PROTEIN_FETCH_VERSIONS_COMPLETED: {} as UntypedPayload,
  PROTEIN_FETCH_VERSIONS_FAILED: {} as UntypedPayload,
  PROTEIN_MERGE: {} as UntypedPayload,
  PROTEIN_OPEN_COMPLETED: {} as UntypedPayload,
  PROTEIN_REMOVE_FROM_SEARCH: {} as UntypedPayload,
  PROTEIN_SEARCH_AMINO_ACIDS: {} as UntypedPayload,
  PROTEIN_SEARCH_OPEN_VIEW: {} as UntypedPayload,
  PROTEIN_SEARCH_OPEN_VIEW_COMPLETED: {} as UntypedPayload,
  PROTEIN_UPDATE_COMPLETED: {} as UntypedPayload,
  PROTEIN_VIEW_NEXT_SEARCH_RESULT: {} as UntypedPayload,
  PROTEIN_VIEW_PREVIOUS_SEARCH_RESULT: {} as UntypedPayload,
  PROTEIN_SELECT_SEARCH_RESULT: {} as UntypedPayload,
  PROTEIN_SET_CURRENT_COORDINATE_STRUCTURE_ID: {} as {
    actionType: 'PROTEIN_SET_CURRENT_COORDINATE_STRUCTURE_ID';
    versionId: number;
    coordinateStructureId: string | null;
  },

  PROTEIN_ALIGNMENT_DELETE_COMPLETED: {} as UntypedPayload,
  PROTEIN_ALIGNMENT_OPEN_COMPLETED: {} as UntypedPayload,
  PROTEIN_ALIGNMENT_RENAME_COMPLETED: {} as UntypedPayload,

  PROTOCOL_OPEN: {} as UntypedPayload,
  PROTOCOL_OPEN_COMPLETED: {} as UntypedPayload,
  PROTOCOL_OPEN_FAILED: {} as UntypedPayload,

  PROTOCOL_UPDATE: {} as UntypedPayload,
  PROTOCOL_UPDATE_COMPLETED: {} as UntypedPayload,
  PROTOCOL_UPDATE_FAILED: {} as UntypedPayload,

  PROTOCOL_DELETE: {} as UntypedPayload,
  PROTOCOL_DELETE_COMPLETED: {} as UntypedPayload,
  PROTOCOL_DELETE_FAILED: {} as UntypedPayload,

  PROTOCOL_COMMENT: {} as UntypedPayload,
  PROTOCOL_COMMENT_COMPLETED: {} as UntypedPayload,
  PROTOCOL_COMMENT_FAILED: {} as UntypedPayload,

  PROTOCOL_DELETE_COMMENT: {} as UntypedPayload,
  PROTOCOL_DELETE_COMMENT_COMPLETED: {} as UntypedPayload,
  PROTOCOL_DELETE_COMMENT_FAILED: {} as UntypedPayload,

  PROTOCOL_STEP_COMPLETE_COMPLETED: {} as UntypedPayload,
  PROTOCOL_STEP_COMPLETE_FAILED: {} as UntypedPayload,
  PROTOCOL_STEP_RESET_COMPLETED: {} as UntypedPayload,
  PROTOCOL_STEP_RESET_FAILED: {} as UntypedPayload,

  PROTOCOL_RESET_STEPS_COMPLETED: {} as UntypedPayload,
  PROTOCOL_RESET_STEPS_FAILED: {} as UntypedPayload,

  ORG_CREATE: {} as UntypedPayload,
  ORG_CREATE_COMPLETED: {} as UntypedPayload,
  ORG_CREATE_FAILED: {} as UntypedPayload,
  ORG_FETCH: {} as UntypedPayload,
  ORG_FETCH_COMPLETED: {} as UntypedPayload,
  ORG_FETCH_FAILED: {} as UntypedPayload,
  ORG_UPDATE: {} as UntypedPayload,
  ORG_UPDATE_COMPLETED: {} as UntypedPayload,
  ORG_UPDATE_FAILED: {} as UntypedPayload,

  TEAM_UPDATE: {} as UntypedPayload,
  TEAM_UPDATE_COMPLETED: {} as UntypedPayload,
  TEAM_UPDATE_FAILED: {} as UntypedPayload,

  ORGANIZATION_CANCEL_INVITE: {} as UntypedPayload,
  ORGANIZATION_SHARE_PROJECTS: {} as UntypedPayload,
  ORGANIZATION_SHARE_PROJECTS_COMPLETED: {} as UntypedPayload,
  ORGANIZATION_SHARE_PROJECTS_FAILED: {} as UntypedPayload,

  MEMBER_PERMISSION_UPDATE: {} as UntypedPayload,
  MEMBER_PERMISSION_UPDATE_COMPLETED: {} as UntypedPayload,
  MEMBER_PERMISSION_UPDATE_FAILED: {} as UntypedPayload,

  MEMBER_REMOVAL: {} as UntypedPayload,
  MEMBER_REMOVAL_COMPLETED: {} as UntypedPayload,
  MEMBER_REMOVAL_FAILED: {} as UntypedPayload,

  GROUP_MEMBERS_FETCH_COMPLETED: {} as UntypedPayload,
  ORG_INITIALS_UPDATE_COMPLETED: {} as UntypedPayload,
  ORG_INITIALS_UPDATE_FAILED: {} as UntypedPayload,

  RESET_CREATE_ORG_MODAL_STATE: {} as UntypedPayload,
  RESET_SEARCH_ORG_MODAL_STATE: {} as UntypedPayload,

  ORG_SEARCH: {} as UntypedPayload,
  ORG_SEARCH_COMPLETED: {} as UntypedPayload,
  ORG_SEARCH_FAILED: {} as UntypedPayload,

  ORDER_FOLDER_TOGGLE_COLLAPSED: {} as UntypedPayload,

  ORDER_SEARCH: {} as UntypedPayload,
  ORDER_SEARCH_COMPLETED: {} as UntypedPayload,
  ORDER_SEARCH_FAILED: {} as UntypedPayload,

  ORDER_OPEN: {} as UntypedPayload,
  ORDER_OPEN_COMPLETED: {} as UntypedPayload,
  ORDER_OPEN_FAILED: {} as UntypedPayload,

  ORDER_UPDATE: {} as UntypedPayload,
  ORDER_UPDATE_COMPLETED: {} as UntypedPayload,
  ORDER_UPDATE_FAILED: {} as UntypedPayload,

  ORDER_DELETE: {} as UntypedPayload,
  ORDER_DELETE_COMPLETED: {} as UntypedPayload,
  ORDER_DELETE_FAILED: {} as UntypedPayload,

  SIDEBAR_OPEN: {} as {
    section?: SidebarSection | null;
  },
  SIDEBAR_CLOSE: {} as {},
  SIDEBAR_OPEN_FOR_FILE_TYPE: {} as {
    fileType: string | null;
  },
  FILE_BROWSER_MOVE_FILES_COMPLETED: {} as UntypedPayload,
  FILE_BROWSER_MOVE_SHORTCUTS_COMPLETED: {} as UntypedPayload,
  FILE_BROWSER_RENAME_FILE: {} as UntypedPayload,
  FILE_BROWSER_RENAME_FILE_FAILED: {} as UntypedPayload,
  FILE_BROWSER_ITEMS_DELETED: {} as UntypedPayload,
  FILE_BROWSER_SOURCE_DELETED: {} as UntypedPayload,
  FILE_BROWSER_SOURCE_UPDATED: {} as UntypedPayload,
  FILE_BROWSER_SOURCES_FETCHED: {} as UntypedPayload,
  FILE_BROWSER_RECENTS_FETCHED: {} as UntypedPayload,
  FILE_BROWSER_ADD_TO_WORKSPACE: {} as UntypedPayload,
  FILE_BROWSER_BULK_REMOVE_FROM_WORKSPACE: {} as UntypedPayload,
  FILE_BROWSER_REMOVE_FROM_WORKSPACE: {} as UntypedPayload,
  FILE_BROWSER_MOVE_WORKSPACE_TAB: {} as UntypedPayload,
  FILE_BROWSER_CLEAR_WORKSPACE: {} as UntypedPayload,
  FILE_BROWSER_UNREGISTER_FILES: {} as UntypedPayload,
  SOURCES_UPDATED_FROM_GRAPHQL: {} as UntypedPayload,
  WORKSPACE_WIDTH_UPDATED: {} as UntypedPayload,

  FILES_FETCH_COMPLETED: {} as UntypedPayload,

  OPEN_SIDEPANEL_WITH_SECTION: {} as {
    section?: SidepanelSection;
  },
  SET_SIDEPANEL_CLOSED: {} as {},
  SET_SIDEPANEL_COLLAPSED: {} as {},
  SET_SIDEPANEL_SEMI_EXPANDED: {} as {
    section?: SidepanelSection;
  },
  SET_SIDEPANEL_FULLY_EXPANDED: {} as {
    section?: SidepanelSection;
  },
  SET_SIDEPANEL_SECTION: {} as {
    section?: SidepanelSection;
  },
  SIDEPANEL_OPEN_CREATE_MENU: {} as {},
  SIDEPANEL_CLOSE_CREATE_MENU: {} as {},
  SIDEPANEL_OPEN_OVERFLOW_MENU: {} as {},
  SIDEPANEL_CLOSE_OVERFLOW_MENU: {} as {},
  SIDEPANEL_LIST_ALL_SOURCES: {} as {
    section: SidepanelSection;
  },
  SIDEPANEL_LIST_SINGLE_SOURCE: {} as {
    section: SidepanelSection;
    folder: StringIDFolder;
  },
  STORABLES_SIDEPANEL_SET_ACTIVE_REGISTRY: {} as {
    registryId: string | null;
  },

  FILE_AUTHORS_UPDATED: {} as UntypedPayload,
  FILE_TAGS_UPDATE_STARTED: {} as UntypedPayload,
  FILE_TAGS_UPDATED: {} as UntypedPayload,
  FILE_TAGS_UPDATE_FAILED: {} as UntypedPayload,
  // TODO(T6458): Get rid of this event
  FILE_TAGS_WITH_FINDER_UPDATED: {} as UntypedPayload,
  FILE_TAG_SCHEMA_UPDATED: {} as UntypedPayload,

  SOURCE_ADD_AUDITOR_COMPLETED: {} as UntypedPayload,
  SOURCE_REMOVE_AUDITOR_COMPLETED: {} as UntypedPayload,

  FOLDER_INVALIDATED: {} as UntypedPayload,

  HOME_DASHBOARD_FETCH_ENTRY_REVIEW_SUMMARY: {} as UntypedPayload,
  HOME_DASHBOARD_FETCH_ENTRY_REVIEW_SUMMARY_COMPLETED: {} as UntypedPayload,
  HOME_DASHBOARD_FETCH_ENTRY_REVIEW_SUMMARY_FAILED: {} as UntypedPayload,

  HOME_DASHBOARD_FETCH_WORKSHEET_REVIEW_SUMMARY: {} as UntypedPayload,
  HOME_DASHBOARD_FETCH_WORKSHEET_REVIEW_SUMMARY_COMPLETED: {} as UntypedPayload,
  HOME_DASHBOARD_FETCH_WORKSHEET_REVIEW_SUMMARY_FAILED: {} as UntypedPayload,

  ONBOARDING_FETCH_GETTING_STARTED_TASKS_COMPLETED: {} as UntypedPayload,
  ONBOARDING_FETCH_EXTRA_SPACE_TASKS_COMPLETED: {} as UntypedPayload,
  ONBOARDING_ACKNOWLEDGE_COMPLETED_MODULE: {} as UntypedPayload,
  ONBOARDING_TOGGLE_COLLAPSED_COMPLETED_NUE_MODULE: {} as UntypedPayload,
  ONBOARDING_OPEN_NUE_MODULE_INDEX: {} as UntypedPayload,
  ONBOARDING_COMPLETE_TASK: {} as UntypedPayload,
  ONBOARDING_DISMISS_NUE_MODULE: {} as UntypedPayload,
  ONBOARDING_RESTORE_NUE_MODULE: {} as UntypedPayload,

  USER_FETCH_ORG_REQUESTS_COMPLETED: {} as UntypedPayload,
  USER_ACCEPT_ORG_REQUEST: {} as UntypedPayload,
  USER_REJECT_ORG_REQUEST: {} as UntypedPayload,
  USER_DISMISS_ALL_ORG_INVITES: {} as UntypedPayload,
  USER_DISMISS_ALL_ORG_INVITES_FAILED: {} as UntypedPayload,
  ORG_ACCEPT_REQUEST: {} as UntypedPayload,
  ORG_REJECT_REQUEST: {} as UntypedPayload,
  USER_FETCH_REFERRALS: {} as UntypedPayload,
  USER_FETCH_REFERRAL_LINK_ID: {} as UntypedPayload,
  USER_FETCH_METADATA: {} as UntypedPayload,

  CLOSE_CRISPR_MODAL: {} as UntypedPayload,
  CLOSE_ORG_INVITE_MODAL: {} as UntypedPayload,
  CLOSE_SEARCH_ORG_MODAL: {} as UntypedPayload,
  OPEN_SEARCH_ORG_MODAL: {} as UntypedPayload,

  CRISPR_ANALYSIS_CREATE: {} as UntypedPayload,
  CRISPR_ANALYSIS_CREATE_COMPLETED: {} as UntypedPayload,
  CRISPR_ANALYSIS_CREATE_FAILED: {} as UntypedPayload,
  CRISPR_ANALYSIS_OPEN: {} as UntypedPayload,
  CRISPR_ANALYSIS_OPEN_COMPLETED: {} as UntypedPayload,
  CRISPR_ANALYSIS_OPEN_FAILED: {} as UntypedPayload,
  CRISPR_ANALYSIS_UPDATE: {} as UntypedPayload,
  CRISPR_ANALYSIS_UPDATE_COMPLETED: {} as UntypedPayload,
  CRISPR_ANALYSIS_UPDATE_FAILED: {} as UntypedPayload,
  CRISPR_ANALYSIS_TOGGLE_SELECTED_REGIONS: {} as UntypedPayload,

  CRISPR_REGION_CREATE: {} as UntypedPayload,
  CRISPR_REGION_CREATE_COMPLETED: {} as UntypedPayload,
  CRISPR_REGION_CREATE_FAILED: {} as UntypedPayload,
  CRISPR_REGION_UPDATE: {} as UntypedPayload,
  CRISPR_REGION_UPDATE_COMPLETED: {} as UntypedPayload,
  CRISPR_REGION_UPDATE_FAILED: {} as UntypedPayload,
  CRISPR_REGIONS_BULK_UPDATE: {} as UntypedPayload,
  CRISPR_REGIONS_BULK_UPDATE_SETTLED: {} as UntypedPayload,
  CRISPR_REGION_ANALYZE_OFF_TARGET_COMPLETED: {} as UntypedPayload,
  CRISPR_REGION_ANALYZE_ON_TARGET_COMPLETED: {} as UntypedPayload,
  CRISPR_REGION_DELETE: {} as UntypedPayload,
  CRISPR_REGION_DELETE_COMPLETED: {} as UntypedPayload,
  CRISPR_REGION_DELETE_FAILED: {} as UntypedPayload,
  CRISPR_ANALYSIS_FETCH_FOR_SEQUENCE: {} as UntypedPayload,
  CRISPR_ANALYSIS_FETCH_FOR_SEQUENCE_COMPLETED: {} as UntypedPayload,
  CRISPR_ANALYSIS_FETCH_FOR_SEQUENCE_FAILED: {} as UntypedPayload,
  CRISPR_ANALYSIS_DELETE: {} as UntypedPayload,
  CRISPR_ANALYSIS_DELETE_COMPLETED: {} as UntypedPayload,
  CRISPR_ANALYSIS_DELETE_FAILED: {} as UntypedPayload,
  CRISPR_ANALYSIS_UPDATE_GUIDE_GROUP_SORT: {} as UntypedPayload,
  CRISPR_ANALYSIS_TOGGLE_SELECTED_GUIDE_GROUPS: {} as UntypedPayload,
  CRISPR_ANALYSIS_ORIGINAL_SEQUENCE_VERSION_FETCH_COMPLETED: {} as UntypedPayload,
  CRISPR_ANALYSIS_REGENERATE: {} as UntypedPayload,
  CRISPR_ANALYSIS_REGENERATE_COMPLETED: {} as UntypedPayload,
  CRISPR_ANALYSIS_REGENERATE_FAILED: {} as UntypedPayload,

  CRISPR_VECTOR_CREATE: {} as UntypedPayload,
  CRISPR_VECTOR_CREATE_COMPLETED: {} as UntypedPayload,
  CRISPR_VECTOR_CREATE_FAILED: {} as UntypedPayload,
  CRISPR_VECTOR_GET_ALL: {} as UntypedPayload,
  CRISPR_VECTOR_GET_ALL_COMPLETED: {} as UntypedPayload,
  CRISPR_VECTOR_GET_ALL_FAILED: {} as UntypedPayload,
  CRISPR_VECTOR_UPDATE: {} as UntypedPayload,
  CRISPR_VECTOR_UPDATE_COMPLETED: {} as UntypedPayload,
  CRISPR_VECTOR_UPDATE_FAILED: {} as UntypedPayload,

  CRISPR_ASSEMBLY_CREATE_ASSEMBLY: {} as UntypedPayload,
  CRISPR_ASSEMBLY_SELECT_VECTOR: {} as UntypedPayload,
  CRISPR_ASSEMBLY_SELECT_SEQUENCE: {} as UntypedPayload,
  CRISPR_ASSEMBLY_SELECT_EXISTING_SEQUENCE: {} as UntypedPayload,
  CRISPR_ASSEMBLY_UPDATE_ASSEMBLY: {} as UntypedPayload,
  CRISPR_ASSEMBLY_FETCH_BIOJS_SEQUENCE: {} as UntypedPayload,

  TRAY_TASK_ADD: {} as {
    taskId: string;
    options: TrayTaskOptions;
  },
  TRAY_TASK_UPDATE: {} as {
    taskId: string;
    options: Partial<TrayTaskOptions>;
  },
  TRAY_TASK_REMOVE: {} as {
    taskId: string;
  },
  TRAY_TASK_TOGGLE_OPEN: {} as EmptyPayload,
  TRAY_TASK_SHOW: {} as EmptyPayload,
  TRAY_TASK_HIDE: {} as EmptyPayload,

  TIMER_UPDATE: {} as {
    timerId: string;
    options: Partial<Timer>;
  },
  TIMER_REMOVE: {} as {
    timerId: string;
  },

  TUTORIAL_FETCH_ALL: {} as UntypedPayload,
  TUTORIAL_FETCH_ALL_COMPLETED: {} as UntypedPayload,
  TUTORIAL_FETCH_ALL_FAILED: {} as UntypedPayload,
  TUTORIAL_OPEN: {} as UntypedPayload,
  TUTORIAL_MARK_COMPLETE: {} as UntypedPayload,
  TUTORIAL_MARK_COMPLETE_COMPLETED: {} as UntypedPayload,
  TUTORIAL_MARK_COMPLETE_FAILED: {} as UntypedPayload,
  TUTORIAL_SET_FILTER: {} as UntypedPayload,
  TUTORIAL_SEARCH: {} as UntypedPayload,
  TUTORIAL_SEARCH_COMPLETED: {} as UntypedPayload,
  TUTORIAL_SEARCH_FAILED: {} as UntypedPayload,
  TUTORIAL_CLEAR_SEARCH: {} as UntypedPayload,

  CALENDAR_CLOSE_CREATE_POPOVER: {} as UntypedPayload,
  CALENDAR_DATE_NAVIGATE: {} as UntypedPayload,
  CALENDAR_DAY_MOUSE_DOWN: {} as UntypedPayload,
  CALENDAR_DAY_MOUSE_OVER: {} as UntypedPayload,
  CALENDAR_DAY_MOUSE_UP: {} as UntypedPayload,
  CALENDAR_ENTRIES_FETCH: {} as UntypedPayload,
  CALENDAR_ENTRIES_FETCH_COMPLETED: {} as UntypedPayload,
  CALENDAR_ENTRIES_FETCH_FAILED: {} as UntypedPayload,
  CALENDAR_FILES_FETCH: {} as UntypedPayload,
  CALENDAR_FILES_FETCH_COMPLETED: {} as UntypedPayload,
  CALENDAR_FILES_FETCH_FAILED: {} as UntypedPayload,
  CALENDAR_PROCESS_TASKS_FETCH: {} as UntypedPayload,
  CALENDAR_PROCESS_TASKS_FETCH_COMPLETED: {} as UntypedPayload,
  CALENDAR_PROCESS_TASKS_FETCH_FAILED: {} as UntypedPayload,
  CALENDAR_REQUESTS_FETCH: {} as UntypedPayload,
  CALENDAR_REQUESTS_FETCH_COMPLETED: {} as UntypedPayload,
  CALENDAR_REQUESTS_FETCH_FAILED: {} as UntypedPayload,
  CALENDAR_ITEM_DESELECTED: {} as UntypedPayload,
  CALENDAR_ITEM_SELECTED: {} as UntypedPayload,
  CALENDAR_TOGGLE_PROJECT_HIDDEN: {} as UntypedPayload,
  CALENDAR_UPDATE_ENTRY_TASK: {} as UntypedPayload,
  CALENDAR_UPDATE_FILTERED_MEMBERS: {} as UntypedPayload,
  CALENDAR_UPDATE_TODAY: {} as UntypedPayload,
  CALENDAR_VIEW_CHANGE: {} as UntypedPayload,

  ORGANISM_CODON_USAGE_FETCH: {} as UntypedPayload,

  HR_TEMPLATE_CREATE: {} as UntypedPayload,
  HR_TEMPLATE_CREATE_COMPLETED: {} as UntypedPayload,
  HR_TEMPLATE_CREATE_FAILED: {} as UntypedPayload,
  HR_TEMPLATE_UPDATE: {} as UntypedPayload,
  HR_TEMPLATE_UPDATE_COMPLETED: {} as UntypedPayload,
  HR_TEMPLATE_UPDATE_FAILED: {} as UntypedPayload,
  HR_TEMPLATE_UPDATE_PENDING_SELECTION: {} as UntypedPayload,
  HR_TEMPLATE_FETCH_FOR_SEQUENCE: {} as UntypedPayload,
  HR_TEMPLATE_FETCH_FOR_SEQUENCE_COMPLETED: {} as UntypedPayload,
  HR_TEMPLATE_FETCH_FOR_SEQUENCE_FAILED: {} as UntypedPayload,

  TFI_ACCOUNT_ADDED: {} as UntypedPayload,
  TFI_ACCOUNT_DELETED: {} as UntypedPayload,
  TFI_ACCOUNTS_FETCHED: {} as UntypedPayload,
  TFI_FOLDER_LISTED: {} as UntypedPayload,
  TFI_FOLDER_CONTENTS_FETCHED: {} as UntypedPayload,
  TFI_FILE_IMPORTED: {} as UntypedPayload,
  TFI_FILE_META_CHECK_FOR_UPDATES: {} as UntypedPayload,
  TFI_FILE_META_CHECK_FOR_UPDATES_COMPLETED: {} as UntypedPayload,
  TFI_FILE_META_REGISTER_CHECK_FOR_UPDATES: {} as UntypedPayload,
  TFI_FILE_META_DEREGISTER_CHECK_FOR_UPDATES: {} as UntypedPayload,
  TFI_SYNC_STARTED: {} as UntypedPayload,
  TFI_FILE_METAS_REFRESHED: {} as UntypedPayload,
  TFI_BULK_REGISTER_REMOTE_REVISION_ID: {} as UntypedPayload,
  TFI_TOGGLE_ADD_MODAL: {} as UntypedPayload,
  TFI_LIST_ACCOUNTS: {} as UntypedPayload,
  TFI_SEARCH_SET_QUERY: {} as UntypedPayload,
  TFI_SEARCH_SET_TFI_ACCOUNT_FILTER: {} as UntypedPayload,
  TFI_SEARCH_CLEAR_SEARCH: {} as UntypedPayload,

  EXPORT_PATENTSAFE_STARTED: {} as UntypedPayload,
  EXPORT_PATENTSAFE_COMPLETED: {} as UntypedPayload,
  EXPORT_PATENTSAFE_FAILED: {} as UntypedPayload,

  TAG_SCHEMA_UPDATED: {} as UntypedPayload,

  BATCH_SCHEMA_SAVING_COMPLETED: {} as UntypedPayload,

  SCHEMA_FIELD_SELECTOR_CREATE_COMPLETED: {} as UntypedPayload,
  SCHEMA_FIELD_SELECTOR_DELETE_COMPLETED: {} as UntypedPayload,
  SCHEMA_FIELD_SELECTOR_UPDATE_CANCELED: {} as UntypedPayload,
  SCHEMA_FIELD_SELECTOR_UPDATE_COMPLETED: {} as UntypedPayload,
  SCHEMA_FIELD_SELECTOR_UPDATE_FAILED: {} as UntypedPayload,
  SCHEMA_FIELD_SELECTORS_FETCH_COMPLETED: {} as UntypedPayload,
  SCHEMA_FIELD_SELECTORS_FETCH_FAILED: {} as UntypedPayload,
  SCHEMA_FIELD_SELECTORS_FETCH_STARTED: {} as UntypedPayload,

  CONTAINER_UPDATE_COMPLETED: {} as UntypedPayload,
  CONTAINER_CONTENT_UPDATE_COMPLETED: {} as UntypedPayload,
  CONTAINER_CONTENTS_DELETE_COMPLETED: {} as UntypedPayload,

  BIOENTITY_ALIAS_INFO_LOAD: {} as UntypedPayload,
  BIOENTITY_ALIAS_INFO_LOAD_COMPLETED: {} as UntypedPayload,
  BIOENTITY_ALIAS_INFO_LOAD_FROM_GRAPHQL: {} as UntypedPayload,
  BIOENTITY_ALIAS_INFO_ADD_COMPLETED: {} as UntypedPayload,
  BIOENTITY_ALIAS_INFO_DELETE_COMPLETED: {} as UntypedPayload,
  BIOENTITY_ALIAS_INFO_UPDATE_COMPLETED: {} as UntypedPayload,
  BIOENTITY_ALIAS_INFO_UPDATE_FAILED: {} as UntypedPayload,
  INVENTORY_FILE_LOAD: {} as UntypedPayload,
  INVENTORY_FILES_UPDATE_REGISTRY_COLLECTIONS: {} as UntypedPayload,

  SPREADSHEET_BULK_IMPORT_BEGIN_IMPORT_FLOW: {} as UntypedPayload,
  SPREADSHEET_BULK_IMPORT_COMPLETED: {} as UntypedPayload,
  SPREADSHEET_BULK_IMPORT_FAILED: {} as UntypedPayload,
  SPREADSHEET_BULK_IMPORT_FIND_DERIVED_COLUMNS: {} as UntypedPayload,
  SPREADSHEET_BULK_IMPORT_FIND_DERIVED_COLUMNS_COMPLETED: {} as UntypedPayload,
  SPREADSHEET_BULK_IMPORT_FIND_DERIVED_COLUMNS_FAILED: {} as UntypedPayload,
  SPREADSHEET_BULK_IMPORT_GO_TO_NEXT_PANE: {} as UntypedPayload,
  SPREADSHEET_BULK_IMPORT_GO_TO_PREV_PANE: {} as UntypedPayload,
  SPREADSHEET_BULK_IMPORT_GO_TO_PANE_WITH_KEY: {} as UntypedPayload,
  SPREADSHEET_BULK_IMPORT_REMOVE_SPREADSHEET: {} as UntypedPayload,
  SPREADSHEET_BULK_IMPORT_RESET: {} as UntypedPayload,
  SPREADSHEET_BULK_IMPORT_SET_SPREADSHEET_COLUMNS_MAPPING_NAME: {} as UntypedPayload,
  SPREADSHEET_BULK_IMPORT_SET_COLUMNS: {} as UntypedPayload,
  SPREADSHEET_BULK_IMPORT_SET_DERIVED_COLUMNS: {} as UntypedPayload,
  SPREADSHEET_BULK_IMPORT_SET_OPTIONS: {} as UntypedPayload,
  SPREADSHEET_BULK_IMPORT_SET_IMPORT_SETTING: {} as UntypedPayload,
  SPREADSHEET_BULK_IMPORT_SET_LANGUAGE_TAG: {} as UntypedPayload,
  SPREADSHEET_BULK_IMPORT_SET_TIME_LIMITS: {} as UntypedPayload,
  SPREADSHEET_BULK_IMPORT_SOURCE_TYPE_CHANGED: {} as UntypedPayload,
  SPREADSHEET_BULK_IMPORT_RAW_TEXT_UPDATED: {} as UntypedPayload,
  SPREADSHEET_BULK_IMPORT_UPLOAD_AND_PARSE: {} as UntypedPayload,
  SPREADSHEET_BULK_IMPORT_UPLOAD_AND_PARSE_COMPLETED: {} as UntypedPayload,
  SPREADSHEET_BULK_IMPORT_UPLOAD_AND_PARSE_FAILED: {} as UntypedPayload,
  SPREADSHEET_BULK_IMPORT_VALIDATE: {} as UntypedPayload,
  SPREADSHEET_BULK_IMPORT_VALIDATE_COMPLETED: {} as UntypedPayload,
  SPREADSHEET_BULK_IMPORT_VALIDATE_FAILED: {} as UntypedPayload,
  SPREADSHEET_BULK_IMPORT_SET_SHOULD_CLOSE_ON_REQUEST: {} as UntypedPayload,
  SPREADSHEET_BULK_IMPORT_UPDATE_MONOMER_MAPPING: {} as UntypedPayload,

  SPREADSHEET_BULK_TRANSFER_STORABLES_COMPLETED: {} as UntypedPayload,

  SPREADSHEET_SET_FORMULA: {} as UntypedPayload,
  SPREADSHEET_SET_TYPED_VALUE: {} as UntypedPayload,
  SPREADSHEET_SET_CELL_EDITOR_POSITION: {} as {
    cellEditorPosition: TableCellEditorPosition | null;
  },

  REGISTRY_ACTIVATED: {} as UntypedPayload,

  LINK_SYNC: {} as {
    added: Array<string>;
    removed: Array<string>;
  },
  LINK_PREVIEWS_FETCHED: {} as {
    previews: Array<Link>;
  },

  RELEVANT_ITEMS_FETCHED: {} as UntypedPayload,
  RELEVANT_ITEMS_REMOVE: {} as UntypedPayload,

  REGISTRY_VALIDATOR_VALIDATE_FILE_TAGS_COMPLETED: {} as UntypedPayload,
  REGISTRY_VALIDATOR_BULK_VALIDATE_FILE_TAGS_COMPLETED: {} as UntypedPayload,
  REGISTRY_VALIDATOR_CHECK: {} as UntypedPayload,
  REGISTRY_VALIDATOR_CHECK_COMPLETED: {} as UntypedPayload,
  REGISTRY_VALIDATOR_MOVE: {} as UntypedPayload,
  REGISTRY_VALIDATOR_CANCEL: {} as UntypedPayload,
  REGISTRY_VALIDATOR_MOVE_COMPLETED: {} as UntypedPayload,
  BIOENTITIES_VALIDATION_STATUS_UPDATED: {} as UntypedPayload,

  TOAST_PUSH: {} as UntypedPayload,
  TOAST_CLOSE: {} as UntypedPayload,

  HIDE_FULL_SCREEN: {} as UntypedPayload,
  SHOW_FULL_SCREEN: {} as UntypedPayload,

  BEGIN_NEW_USER_FLOW: {} as UntypedPayload,
  NEW_USER_FLOW_IS_LOADING: {} as UntypedPayload,
  NEW_USER_FLOW_DONE_LOADING: {} as UntypedPayload,
  SHOW_NEXT_NEW_USER_FLOW_SCREEN: {} as UntypedPayload,
  UPDATE_NEW_USER_FLOW_PROJECT: {} as UntypedPayload,

  OPEN_CREATE_ORGANIZATION_PAGE: {} as UntypedPayload,
  OPEN_CREATE_TEAM_PAGE: {} as UntypedPayload,
  OPEN_CREATE_TEAM_PAGE_FAILED: {} as UntypedPayload,
  OPEN_ORGANIZATION_PAGE: {} as UntypedPayload,
  OPEN_ORGANIZATION_PAGE_COMPLETED: {} as UntypedPayload,
  OPEN_ORGANIZATION_PAGE_FAILED: {} as UntypedPayload,
  OPEN_TEAM_PAGE: {} as UntypedPayload,
  OPEN_TEAM_PAGE_COMPLETED: {} as UntypedPayload,
  OPEN_TEAM_PAGE_FAILED: {} as UntypedPayload,
  OPEN_USER_PAGE_COMPLETED: {} as UntypedPayload,
  OPEN_MEMBERS_TAB_COMPLETED: {} as UntypedPayload,
  OPEN_ORGANIZATION_PROJECTS_TAB: {} as UntypedPayload,
  OPEN_TEAM_PROJECTS_TAB: {} as UntypedPayload,
  OPEN_ORGANIZATION_TEAMS_TAB: {} as UntypedPayload,
  UPDATE_ACTIVE_TAB: {} as UntypedPayload,

  REGISTRY_ACTIVATION_SET_ORGANIZATION: {} as UntypedPayload,
  REGISTRY_ACTIVATION_SET_IS_LOADING: {} as UntypedPayload,

  APPLICABLE_BATCHES_FETCH_COMPLETED: {} as UntypedPayload,
  BATCHES_FETCH_COMPLETED: {} as UntypedPayload,
  BATCHES_IMPORT_COMPLETED: {} as UntypedPayload,
  BATCHES_BULK_UPDATE_COMPLETED: {} as UntypedPayload,
  CONTAINER_BULK_UPDATE_COMPLETED: {} as UntypedPayload,
  CONTAINER_TAGS_UPDATE_COMPLETED: {} as UntypedPayload,
  CONTAINER_FETCH_COMPLETED: {} as UntypedPayload,
  CONTAINER_FETCH_TRANSFER_INFO_COMPLETED: {} as UntypedPayload,
  CONTAINER_BULK_FETCH_COMPLETED: {} as UntypedPayload,
  CONTAINER_INVALIDATED: {} as UntypedPayload,
  CONTAINER_SCHEMAS_FETCH_COMPLETED: {} as UntypedPayload,
  CONTAINER_SCHEMA_CREATE_COMPLETED: {} as UntypedPayload,
  CONTAINER_SCHEMA_DELETE_COMPLETED: {} as UntypedPayload,
  CONTAINER_SCHEMA_UPDATE: {} as UntypedPayload,
  CONTAINER_SCHEMA_UPDATE_COMPLETED: {} as UntypedPayload,
  CONTAINER_SCHEMA_UPDATE_FAILED: {} as UntypedPayload,
  CONTAINER_SCHEMAS_UPDATED_FROM_GRAPHQL: {} as UntypedPayload,

  STORABLE_SUMMARIES_FETCH_COMPLETED: {} as UntypedPayload,
  STORABLES_BULK_FETCH_COMPLETED: {} as UntypedPayload,
  STORABLES_UPDATED: {} as UntypedPayload,
  STORABLES_MOVE_COMPLETED: {} as UntypedPayload,

  CONTAINERS_TRANSFER_COMPLETED: {} as UntypedPayload,
  CONTAINERS_TRANSFER_UNDO_COMPLETED: {} as UntypedPayload,

  BATCHES_BULK_FETCH_COMPLETED: {} as UntypedPayload,
  BOX_SCHEMAS_FETCH_COMPLETED: {} as UntypedPayload,
  BOX_SCHEMA_CREATE_COMPLETED: {} as UntypedPayload,
  BOX_SCHEMA_DELETE_COMPLETED: {} as UntypedPayload,
  BOX_SCHEMA_UPDATE: {} as UntypedPayload,
  BOX_SCHEMA_UPDATE_COMPLETED: {} as UntypedPayload,
  BOX_SCHEMA_UPDATE_FAILED: {} as UntypedPayload,
  BOX_SCHEMAS_UPDATED_FROM_GRAPHQL: {} as UntypedPayload,
  PLATE_SCHEMAS_FETCH_COMPLETED: {} as UntypedPayload,
  PLATE_SCHEMA_CREATE_COMPLETED: {} as UntypedPayload,
  PLATE_SCHEMA_DELETE_COMPLETED: {} as UntypedPayload,
  PLATE_SCHEMA_UPDATE: {} as UntypedPayload,
  PLATE_SCHEMA_UPDATE_COMPLETED: {} as UntypedPayload,
  PLATE_SCHEMA_UPDATE_FAILED: {} as UntypedPayload,
  PLATE_SCHEMAS_UPDATED_FROM_GRAPHQL: {} as UntypedPayload,
  GRID_FETCH_COMPLETED: {} as UntypedPayload,
  GRID_CLOSE: {} as UntypedPayload,
  GRID_INVALIDATED: {} as UntypedPayload,
  GRID_UPDATE_STARTED: {} as UntypedPayload,
  GRID_UPDATE_COMPLETED: {} as UntypedPayload,
  GRID_UPDATE_FAILED: {} as UntypedPayload,
  GRIDS_TRANSFER_STARTED: {} as UntypedPayload,
  GRIDS_TRANSFER_COMPLETED: {} as UntypedPayload,
  GRIDS_TRANSFER_FAILED: {} as UntypedPayload,
  GRID_VALIDATE_UPLOAD_CONTAINERS_COMPLETED: {} as UntypedPayload,
  GRID_VALIDATE_UPLOAD_CONTAINERS_FAILED: {} as UntypedPayload,
  STORABLE_FETCH_FAILED_WITH_UNREADABLE_SAMPLE_ERROR: {} as UntypedPayload,
  STORABLE_FETCH_FAILED_WITH_INACCESSIBLE_ERROR: {} as UntypedPayload,

  LOCATION_FETCH_COMPLETED: {} as UntypedPayload,
  LOCATION_SCHEMAS_FETCH_COMPLETED: {} as UntypedPayload,
  LOCATION_SCHEMA_CREATE_COMPLETED: {} as UntypedPayload,
  LOCATION_SCHEMA_DELETE_COMPLETED: {} as UntypedPayload,
  LOCATION_SCHEMA_UPDATE: {} as UntypedPayload,
  LOCATION_SCHEMA_UPDATE_COMPLETED: {} as UntypedPayload,
  LOCATION_SCHEMA_UPDATE_FAILED: {} as UntypedPayload,
  LOCATION_SCHEMAS_UPDATED_FROM_GRAPHQL: {} as UntypedPayload,

  STORABLE_STORE_RESET_SAVING_STATE: {} as UntypedPayload,

  PRINTERS_FETCH_COMPLETED: {} as UntypedPayload,
  PRINTER_CREATE_COMPLETED: {} as UntypedPayload,
  PRINTER_UPDATE_STARTED: {} as UntypedPayload,
  PRINTER_UPDATE_FAILED: {} as UntypedPayload,
  PRINTER_UPDATE_COMPLETED: {} as UntypedPayload,
  PRINTER_DELETE_COMPLETED: {} as UntypedPayload,
  LABEL_TEMPLATES_FETCH_COMPLETED: {} as UntypedPayload,
  LABEL_TEMPLATE_CREATE_COMPLETED: {} as UntypedPayload,
  LABEL_TEMPLATE_UPDATE_STARTED: {} as UntypedPayload,
  LABEL_TEMPLATE_UPDATE_COMPLETED: {} as UntypedPayload,
  LABEL_TEMPLATE_UPDATE_FAILED: {} as UntypedPayload,
  LABEL_TEMPLATE_DELETE_COMPLETED: {} as UntypedPayload,

  ASSAY_SCHEMAS_LISTED: {} as UntypedPayload,
  ASSAY_SCHEMA_FETCHED: {} as UntypedPayload,
  ASSAY_SCHEMAS_UPDATED_FROM_GQL: {} as UntypedPayload,

  WORKFLOW_SELECT_WORKFLOW_TEMPLATE_VERSION: {} as UntypedPayload,
  WORKFLOW_WORKFLOW_DASHBOARD_SCREEN_OPEN: {} as UntypedPayload,
  WORKFLOW_WORKFLOW_DASHBOARD_TEMPLATES_SCREEN_OPEN: {} as UntypedPayload,
  WORKFLOW_WORKFLOW_DETAILS_SCREEN_OPEN: {} as UntypedPayload,
  WORKFLOW_WORKFLOW_TEMPLATE_CONFIGURE_SCREEN_OPEN: {} as UntypedPayload,
  WORKFLOW_WORKFLOW_TEMPLATE_VIEW_SCREEN_OPEN: {} as UntypedPayload,

  WORKFLOW_ALL_DATA_TABLE_CONFIGS_FETCH_COMPLETED: {} as UntypedPayload,

  WORKFLOW_WORKFLOW_SUMMARIES_FETCH_COMPLETED: {} as UntypedPayload,
  WORKFLOW_ALL_WORKFLOW_TEMPLATES_FETCH_COMPLETED: {} as UntypedPayload,
  WORKFLOW_WORKFLOW_DETAILS_FETCH_COMPLETED: {} as UntypedPayload,
  WORKFLOW_WORKFLOW_FETCH_COMPLETED: {} as UntypedPayload,
  WORKFLOW_WORKFLOW_TEMPLATE_FULL_FETCH_COMPLETED: {} as UntypedPayload,
  WORKFLOW_WORKFLOW_TEMPLATE_VERSION_FETCH_COMPLETED: {} as UntypedPayload,
  WORKFLOW_WORKFLOW_TEMPLATE_VERSION_DETAILS_FETCH_COMPLETED: {} as UntypedPayload,
  WORKFLOW_WORKFLOW_TEMPLATE_VERSION_SUMMARY_FETCH_COMPLETED: {} as UntypedPayload,
  WORKFLOW_STAGE_CONFIGURATION_FETCH_COMPLETED: {} as UntypedPayload,
  WORKFLOW_STAGE_FETCH_COMPLETED: {} as UntypedPayload,

  WORKFLOW_STAGE_ENTRIES_FETCH_COMPLETED: {} as UntypedPayload,
  WORKFLOW_STAGE_ENTRY_DETAILS_FETCH_COMPLETED: {} as UntypedPayload,
  WORKFLOW_STAGE_ENTRY_DOCUMENT_FETCH_STARTED: {} as UntypedPayload,
  WORKFLOW_STAGE_ENTRY_DOCUMENT_FETCH_COMPLETED: {} as UntypedPayload,
  WORKFLOW_STAGE_ENTRY_DOCUMENT_FETCH_FAILED: {} as UntypedPayload,
  WORKFLOW_STAGE_ENTRY_INVALIDATE: {} as UntypedPayload,
  WORKFLOW_STAGE_ENTRY_REGISTER_OBJECTS: {} as UntypedPayload,
  WORKFLOW_STAGE_ENTRY_CLOSE: {} as UntypedPayload,
  WORKFLOW_STAGE_ENTRY_REVERT: {} as UntypedPayload,
  WORKFLOW_STAGE_ENTRY_UPDATE: {} as UntypedPayload,
  WORKFLOW_STAGE_ENTRY_SAVE: {} as UntypedPayload,
  WORKFLOW_STAGE_ENTRY_SAVE_COMPLETED: {} as UntypedPayload,
  WORKFLOW_STAGE_ENTRY_SAVE_FAILED: {} as UntypedPayload,
  WORKFLOW_STAGE_ENTRY_LOAD_VERSION: {} as UntypedPayload,
  WORKFLOW_STAGE_ENTRY_LOAD_VERSION_COMPLETED: {} as UntypedPayload,
  WORKFLOW_STAGE_ENTRY_LOAD_VERSION_FAILED: {} as UntypedPayload,
  WORKFLOW_STAGE_ENTRY_FETCH_VERSIONS: {} as UntypedPayload,
  WORKFLOW_STAGE_ENTRY_FETCH_VERSIONS_COMPLETED: {} as UntypedPayload,
  WORKFLOW_STAGE_ENTRY_FETCH_VERSIONS_FAILED: {} as UntypedPayload,
  WORKFLOW_STAGE_ENTRY_SERVER_SIDE_UPDATE: {} as UntypedPayload,

  WORKFLOW_STAGE_FLOW_NODE_FETCH_COMPLETED: {} as UntypedPayload,

  WORKFLOW_ENTRY_TEMPLATE_FETCH_STARTED: {} as UntypedPayload,
  WORKFLOW_ENTRY_TEMPLATE_FETCH_COMPLETED: {} as UntypedPayload,
  WORKFLOW_ENTRY_TEMPLATE_FETCH_FAILED: {} as UntypedPayload,
  WORKFLOW_ENTRY_TEMPLATE_DISCARD: {} as UntypedPayload,
  WORKFLOW_ENTRY_TEMPLATE_REGISTER_OBJECTS: {} as UntypedPayload,
  WORKFLOW_ENTRY_TEMPLATE_UPDATE: {} as UntypedPayload,
  WORKFLOW_ENTRY_TEMPLATE_SAVE_STARTED: {} as UntypedPayload,
  WORKFLOW_ENTRY_TEMPLATE_SAVE_COMPLETED: {} as UntypedPayload,
  WORKFLOW_ENTRY_TEMPLATE_SAVE_FAILED: {} as UntypedPayload,

  WORKFLOW_STAGE_RUN_SAMPLES_FETCH_COMPLETED: {} as UntypedPayload,
  WORKFLOW_STAGE_RUN_SAMPLES_FETCH_STARTED: {} as UntypedPayload,
  WORKFLOW_STAGE_RUNS_FETCH_COMPLETED: {} as UntypedPayload,
  WORKFLOW_STAGE_RUNS_OUTPUT_SAMPLES_FETCHED: {} as UntypedPayload,

  WORKFLOW_GRAPH_CHANGED: {} as UntypedPayload,

  BULK_ANTIBODY_UPDATE: {} as UntypedPayload,

  BULK_ASSEMBLY_SET_TOPOLOGY: {} as UntypedPayload,
  BULK_ASSEMBLY_ADD_SLOT: {} as UntypedPayload,
  BULK_ASSEMBLY_NEXT_SLOT: {} as UntypedPayload,
  BULK_ASSEMBLY_PREVIOUS_SLOT: {} as UntypedPayload,
  BULK_ASSEMBLY_SET_SLOT_DATA_SOURCE: {} as UntypedPayload,
  BULK_ASSEMBLY_UPDATE_SLOT: {} as UntypedPayload,
  BULK_ASSEMBLY_REMOVE_SLOT: {} as UntypedPayload,
  BULK_ASSEMBLY_UPDATE_DATA_SOURCE: {} as UntypedPayload,
  BULK_ASSEMBLY_ACTIVATE_STEP: {} as UntypedPayload,
  BULK_ASSEMBLY_RESET_SLOT_DATA_SOURCE: {} as UntypedPayload,
  BULK_ASSEMBLY_SET_SLOT_DATA_SOURCE_TYPE: {} as UntypedPayload,
  BULK_ASSEMBLY_RUN: {} as UntypedPayload,
  BULK_ASSEMBLY_RUN_COMPLETED: {} as UntypedPayload,
  BULK_ASSEMBLY_RUN_FAILED: {} as UntypedPayload,
  BULK_ASSEMBLY_TAG_SCHEMA_ID: {} as UntypedPayload,
  BULK_ASSEMBLY_RESET_SCHEMA_FIELDS: {} as UntypedPayload,
  BULK_ASSEMBLY_SEQUENCE_FOLDER_ID: {} as UntypedPayload,
  BULK_ASSEMBLY_PRIMER_FOLDER_ID: {} as UntypedPayload,
  BULK_ASSEMBLY_FRAGMENT_FOLDER_ID: {} as UntypedPayload,
  BULK_ASSEMBLY_RESET: {} as UntypedPayload,
  BULK_ASSEMBLY_RESET_SOURCES_AND_OUTPUT: {} as UntypedPayload,
  BULK_ASSEMBLY_VALIDATE: {} as UntypedPayload,
  BULK_ASSEMBLY_VALIDATE_COMPLETED: {} as UntypedPayload,
  BULK_ASSEMBLY_VALIDATE_FAILED: {} as UntypedPayload,
  BULK_ASSEMBLY_SET_USE_SEQUENCE_WORKLIST: {} as UntypedPayload,
  BULK_ASSEMBLY_SET_USE_PRIMER_WORKLIST: {} as UntypedPayload,
  BULK_ASSEMBLY_SET_USE_FRAGMENT_WORKLIST: {} as UntypedPayload,
  BULK_ASSEMBLY_SET_NEW_SEQUENCE_WORKLIST_INFO: {} as UntypedPayload,
  BULK_ASSEMBLY_SET_NEW_PRIMER_WORKLIST_INFO: {} as UntypedPayload,
  BULK_ASSEMBLY_SET_NEW_FRAGMENT_WORKLIST_INFO: {} as UntypedPayload,
  BULK_ASSEMBLY_SET_EXISTING_SEQUENCE_WORKLIST_ID: {} as UntypedPayload,
  BULK_ASSEMBLY_SET_EXISTING_PRIMER_WORKLIST_ID: {} as UntypedPayload,
  BULK_ASSEMBLY_SET_EXISTING_FRAGMENT_WORKLIST_ID: {} as UntypedPayload,
  BULK_ASSEMBLY_SET_NEW_OR_EXISTING_SEQUENCE_WORKLIST: {} as UntypedPayload,
  BULK_ASSEMBLY_SET_NEW_OR_EXISTING_PRIMER_WORKLIST: {} as UntypedPayload,
  BULK_ASSEMBLY_SET_NEW_OR_EXISTING_FRAGMENT_WORKLIST: {} as UntypedPayload,
  BULK_ASSEMBLY_UPDATE_GIBSON_PARAMETERS: {} as UntypedPayload,

  BULK_TRANSLATE_UPDATE: {} as UntypedPayload,
  BULK_TRANSLATE_UPDATE_TRANSLATION_SPEC: {} as UntypedPayload,
  BULK_TRANSLATE_RUN_COMPLETED: {} as UntypedPayload,

  BULK_ARCHIVE_ASSAY_SCHEMAS_COMPLETED: {} as UntypedPayload,
  BULK_ARCHIVE_BATCHES_COMPLETED: {} as UntypedPayload,
  BULK_ARCHIVE_CONNECTIONS: {} as UntypedPayload,
  BULK_ARCHIVE_FILES_COMPLETED: {} as UntypedPayload,
  BULK_ARCHIVE_ANALYSES_COMPLETED: {} as UntypedPayload,
  BULK_ARCHIVE_PROCESS_MANAGEMENT: {} as UntypedPayload,
  BULK_ARCHIVE_SOURCES_COMPLETED: {} as UntypedPayload,
  BULK_ARCHIVE_REGISTRIES_COMPLETED: {} as UntypedPayload,
  BULK_ARCHIVE_STORABLES_COMPLETED: {} as UntypedPayload,
  BULK_ARCHIVE_TAG_SCHEMAS_COMPLETED: {} as UntypedPayload,
  BULK_ARCHIVE_TEMPLATE_COLLECTIONS_COMPLETED: {} as UntypedPayload,
  BULK_ARCHIVE_STORABLE_SCHEMAS_COMPLETED: {} as UntypedPayload,
  BULK_ARCHIVE_SCHEMA_FIELD_SELECTORS_COMPLETED: {} as UntypedPayload,
  BULK_UNARCHIVE_ASSAY_SCHEMAS_COMPLETED: {} as UntypedPayload,
  BULK_UNARCHIVE_BATCHES_COMPLETED: {} as UntypedPayload,
  BULK_UNARCHIVE_CONNECTIONS: {} as UntypedPayload,
  BULK_UNARCHIVE_FILES_COMPLETED: {} as UntypedPayload,
  BULK_UNARCHIVE_ANALYSES_COMPLETED: {} as UntypedPayload,
  BULK_UNARCHIVE_PROCESS_MANAGEMENT: {} as UntypedPayload,
  BULK_UNARCHIVE_SOURCES_COMPLETED: {} as UntypedPayload,
  BULK_UNARCHIVE_STORABLES_COMPLETED: {} as UntypedPayload,
  BULK_UNARCHIVE_TAG_SCHEMAS_COMPLETED: {} as UntypedPayload,
  BULK_UNARCHIVE_TEMPLATE_COLLECTIONS_COMPLETED: {} as UntypedPayload,
  BULK_UNARCHIVE_STORABLE_SCHEMAS_COMPLETED: {} as UntypedPayload,
  BULK_UNARCHIVE_SCHEMA_FIELD_SELECTORS_COMPLETED: {} as UntypedPayload,

  HOISTED_TAGS_FETCH_COMPLETED: {} as UntypedPayload,

  CONTAINABLE_LIST_CONTAINERS_STARTED: {} as UntypedPayload,
  CONTAINABLE_LIST_PLATES_STARTED: {} as UntypedPayload,
  CONTAINABLE_LIST_CONTAINERS_COMPLETED: {} as UntypedPayload,
  CONTAINABLE_LIST_PLATES_COMPLETED: {} as UntypedPayload,
  CONTAINABLE_LIST_CONTAINERS_FAILED: {} as UntypedPayload,
  CONTAINABLE_LIST_PLATES_FAILED: {} as UntypedPayload,
  CONTAINABLE_CLEAR_CONTAINER_LISTING: {} as UntypedPayload,
  CONTAINABLE_CLEAR_PLATE_LISTING: {} as UntypedPayload,

  COMPUTED_TAGS_UPDATED_AFTER_REGISTRATION_ACTION: {} as UntypedPayload,

  CICO_STATUS_UPDATED: {} as UntypedPayload,

  PROCESS_MANAGEMENT_ITEMS_UPDATED: {} as UntypedPayload,
} as const;

/**
 * A string literal enum of all possible event names.
 */
export const EventConstants = Object.keys(EVENT_PAYLOADS).reduce<Record<string, string>>((acc, key) => {
  acc[key] = key;
  return acc;
}, {}) as {[K in keyof typeof EVENT_PAYLOADS]: K};

type EventNames = keyof typeof EVENT_PAYLOADS;

/**
 * A discriminated union of all possible event payload types.
 */
export type FluxAction = {
  [EventName in EventNames]: {actionType: EventName} & (typeof EVENT_PAYLOADS)[EventName];
}[EventNames];

export interface DocumentUpdateSpec {
  id: string;
  spec: Spec<Document>;
}
